import { BaseControlItem } from '@bloomfieldrobotics/canopy';
import { ButtonBase, Collapse, Fade, Typography } from '@mui/material';
import { useState } from 'react';
import { useUnits } from '../../../../settings/hooks/useUnits';
import { getBerrySizeLabel } from './constants';
import { UniformityForm } from './UniformityForm';

type Props = {
  clusterConsistency: number;
  minSize: number;
  maxSize: number;
  onSubmit: (
    clusterConsistency: number,
    minSize: number,
    maxSize: number
  ) => void;
};

/**
 * Uniformity control item - a controlled input that opens the uniformity
 * form and displays the friendly filter description
 * @param {number} clusterConsistency - cluster consistency, percentage
 * @param {number} minSize - min size, 0 means no min size
 * @param {number} maxSize - max size, 0 means no max size
 * @param {function} onSubmit - on submit callback
 */
export const UniformityControlItem = ({
  clusterConsistency,
  minSize,
  maxSize,
  onSubmit,
}: Props) => {
  const units = useUnits();
  const [uniformityFormOpen, setUniformityFormOpen] = useState(false);

  const handleClick = () => {
    setUniformityFormOpen((prev) => !prev);
  };

  let displayText = '';
  if (!clusterConsistency && !minSize && !maxSize) {
    displayText = 'None';
  } else if (!maxSize) {
    displayText = `${clusterConsistency}%+ ${getBerrySizeLabel(
      minSize,
      units
    )}+`;
  } else if (!minSize) {
    displayText = `${clusterConsistency}%+ up to ${getBerrySizeLabel(
      maxSize,
      units
    )}`;
  } else {
    displayText = `${clusterConsistency}%+ ${getBerrySizeLabel(
      minSize,
      units
    )}-${getBerrySizeLabel(maxSize, units)}`;
  }

  return (
    <>
      <BaseControlItem label={'Uniformity'}>
        <ButtonBase disableRipple onClick={handleClick}>
          <Typography variant='body2' sx={{ fontWeight: 500 }}>
            {displayText}
          </Typography>
        </ButtonBase>
      </BaseControlItem>
      <Collapse
        in={uniformityFormOpen}
        sx={{ padding: 6, paddingTop: 0, paddingBottom: 0 }}
      >
        <Fade in={uniformityFormOpen}>
          <div>
            <UniformityForm
              initClusterConsistency={clusterConsistency}
              initMinSize={minSize}
              initMaxSize={maxSize}
              onSubmit={(clusterConsistency, minSize, maxSize) => {
                onSubmit(clusterConsistency, minSize, maxSize);
                setUniformityFormOpen(false);
              }}
            />
          </div>
        </Fade>
      </Collapse>
    </>
  );
};
